// chartDataPrep.js - Chart data formatting for team comparison
/**
 * Prepare line chart data for team comparison
 * @param {Object} team1Scores - First team scores object
 * @param {Object} team2Scores - Second team scores object  
 * @param {Object} tredeNames - Trede name mappings
 * @returns {Object} Chart.js/Recharts formatted data
 */
export const prepareLineChartData = (team1Scores, team2Scores, tredeNames) => {
  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
  
  const data = tredes.map(trede => ({
    trede: tredeNames[trede],
    team1: Math.round((team1Scores[trede] || 0) * 10) / 10,
    team2: Math.round((team2Scores[trede] || 0) * 10) / 10,
    difference: Math.round(((team1Scores[trede] || 0) - (team2Scores[trede] || 0)) * 10) / 10
  }));
  
  return {
    data,
    datasets: [
      {
        name: 'Team 1',
        dataKey: 'team1',
        stroke: '#3b82f6',
        strokeWidth: 3,
        dot: { fill: '#3b82f6', strokeWidth: 2, r: 6 }
      },
      {
        name: 'Team 2', 
        dataKey: 'team2',
        stroke: '#10b981',
        strokeWidth: 3,
        dot: { fill: '#10b981', strokeWidth: 2, r: 6 }
      }
    ],
    xAxisDataKey: 'trede',
    yAxisDomain: [1, 5],
    gridConfig: {
      horizontal: true,
      vertical: false,
      strokeDasharray: '3 3',
      stroke: '#e5e7eb'
    }
  };
};

/**
 * Prepare spider/radar chart data for team comparison  
 * @param {Object} team1Scores - First team scores
 * @param {Object} team2Scores - Second team scores
 * @param {Object} tredeNames - Trede name mappings
 * @returns {Object} Radar chart data with transparency/colors
 */
export const prepareSpiderChartData = (team1Scores, team2Scores, tredeNames) => {
  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
  
  const data = tredes.map(trede => ({
    subject: tredeNames[trede],
    team1: Math.round((team1Scores[trede] || 0) * 10) / 10,
    team2: Math.round((team2Scores[trede] || 0) * 10) / 10,
    fullMark: 5
  }));
  
  return {
    data,
    datasets: [
      {
        name: 'Team 1',
        dataKey: 'team1',
        stroke: '#3b82f6',
        fill: '#3b82f6',
        fillOpacity: 0.3,
        strokeWidth: 2
      },
      {
        name: 'Team 2',
        dataKey: 'team2', 
        stroke: '#10b981',
        fill: '#10b981',
        fillOpacity: 0.3,
        strokeWidth: 2
      }
    ],
    polarGridConfig: {
      gridType: 'polygon',
      radialLines: true
    },
    polarAngleAxisConfig: {
      tick: true,
      tickLine: false,
      axisLine: false
    },
    polarRadiusAxisConfig: {
      angle: 90,
      domain: [0, 5],
      tick: true,
      tickCount: 6
    }
  };
};

/**
 * Prepare comparison table data for detailed view
 * @param {Object} team1 - First team data
 * @param {Object} team2 - Second team data  
 * @param {Object} comparisonResults - Comparison analysis results
 * @returns {Object} Formatted table data
 */
export const prepareComparisonTableData = (team1, team2, comparisonResults) => {
  const { tredeComparisons } = comparisonResults;
  
  const tableData = Object.values(tredeComparisons).map(comp => ({
    trede: comp.name,
    team1Score: comp.team1Score,
    team2Score: comp.team2Score,
    difference: comp.difference,
    percentageChange: comp.percentageChange,
    trend: comp.trend,
    significance: comp.significance,
    formattedDifference: formatScoreDifference(comp.team1Score, comp.team2Score) // omgedraaid zodat het consistent is met berekening in teamComparison.js
  }));
  
  return {
    tableData,
    headers: [
      { key: 'trede', label: 'Lencioni Trede', width: '25%' },
      { key: 'team1Score', label: team1.name, width: '15%' },
      { key: 'team2Score', label: team2.name, width: '15%' },
      { key: 'formattedDifference', label: 'Verschil', width: '20%' },
      { key: 'trend', label: 'Trend', width: '15%' },
      { key: 'significance', label: 'Impact', width: '10%' }
    ],
    summary: {
      team1Average: Math.round(Object.values(tredeComparisons).reduce((sum, comp) => sum + comp.team1Score, 0) / Object.keys(tredeComparisons).length * 10) / 10,
      team2Average: Math.round(Object.values(tredeComparisons).reduce((sum, comp) => sum + comp.team2Score, 0) / Object.keys(tredeComparisons).length * 10) / 10,
      overallDifference: comparisonResults.overallAnalysis.averageDifference,
      overallTrend: comparisonResults.overallAnalysis.overallTrend
    }
  };
};

/**
 * Format score difference with trend indicator and color
 * @param {number} oldScore - Previous score
 * @param {number} newScore - Current score  
 * @returns {Object} Formatted difference with styling
 */
export const formatScoreDifference = (oldScore, newScore) => {
  const difference = newScore - oldScore;
  const percentageChange = oldScore !== 0 ? ((difference / oldScore) * 100) : 0;
  
  let trend = 'stable';
  let color = '#6b7280'; // gray
  let icon = '➡️';
  
  if (difference > 0.1) {
    trend = 'up';
    color = '#10b981'; // green
    icon = '📈';
  } else if (difference < -0.1) {
    trend = 'down';
    color = '#ef4444'; // red
    icon = '📉';
  }
  
  const formattedDiff = Math.abs(difference).toFixed(1);
  const formattedPercentage = Math.abs(percentageChange).toFixed(1);
  
  return {
    difference: difference,
    formattedDifference: difference === 0 ? '0.0' : `${difference > 0 ? '+' : '-'}${formattedDiff}`,
    percentageChange: percentageChange,
    formattedPercentage: `${percentageChange > 0 ? '+' : '-'}${formattedPercentage}%`,
    trend,
    color,
    icon,
    displayText: difference === 0 ? '0.0' : `${difference > 0 ? '+' : ''}${difference.toFixed(1)}`,
    displayColor: color,
    trendIcon: icon
  };
};

/**
 * Prepare chart data for insight visualization (bar chart showing differences)
 * @param {Object} comparisonResults - Comparison analysis
 * @returns {Object} Bar chart data for insights
 */
export const prepareInsightChartData = (comparisonResults) => {
  const { tredeComparisons } = comparisonResults;
  
  const data = Object.values(tredeComparisons).map(comp => ({
    trede: comp.name,
    difference: comp.difference,
    absoluteDifference: Math.abs(comp.difference),
    color: comp.difference > 0 ? '#10b981' : comp.difference < 0 ? '#ef4444' : '#6b7280',
    trend: comp.trend
  }));
  
  return {
    data,
    chartConfig: {
      type: 'bar',
      dataKey: 'difference',
      nameKey: 'trede',
      colors: {
        positive: '#10b981',
        negative: '#ef4444',
        neutral: '#6b7280'
      },
      yAxisDomain: [-2, 2], // Score differences typically range -2 to +2
      referenceLines: [
        { y: 0, stroke: '#374151', strokeWidth: 2 }
      ]
    }
  };
};

/**
 * Prepare progress comparison data (for multiple measurements over time)
 * @param {Array} measurements - Array of measurement data over time
 * @param {string} focusTrede - Trede to focus on
 * @returns {Object} Time series chart data
 */
export const prepareProgressChartData = (measurements, focusTrede = null) => {
  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
  const tredeNames = {
    vertrouwen: 'Vertrouwen',
    conflicten: 'Conflicten',
    betrokkenheid: 'Commitment',
    verantwoording: 'Verantwoording',
    resultaten: 'Gezamenlijk belang' // veranderd nav gebruikersfeedback
  };
  
  const data = measurements.map(measurement => {
    const dataPoint = {
      period: measurement.period || measurement.measurementPeriod,
      date: measurement.measuredAt || measurement.createdAt
    };
    
    // Add all trede scores
    tredes.forEach(trede => {
      dataPoint[trede] = Math.round((measurement.teamScores[trede] || 0) * 10) / 10;
    });
    
    return dataPoint;
  });
  
  // Create datasets for each trede or focus on specific trede
  const datasets = focusTrede ? 
    [{
      name: tredeNames[focusTrede],
      dataKey: focusTrede,
      stroke: '#3b82f6',
      strokeWidth: 3,
      dot: { fill: '#3b82f6', strokeWidth: 2, r: 6 }
    }] :
    tredes.map((trede, index) => ({
      name: tredeNames[trede],
      dataKey: trede,
      stroke: `hsl(${index * 72}, 70%, 50%)`, // Distribute colors evenly
      strokeWidth: 2,
      dot: { strokeWidth: 2, r: 4 }
    }));
  
  return {
    data,
    datasets,
    xAxisDataKey: 'period',
    yAxisDomain: [1, 5],
    gridConfig: {
      horizontal: true,
      vertical: true,
      strokeDasharray: '3 3',
      stroke: '#e5e7eb'
    }
  };
};

/**
 * Calculate chart dimensions based on container and data
 * @param {Object} containerDimensions - Container width/height
 * @param {string} chartType - Type of chart (line, radar, bar)
 * @returns {Object} Optimal chart dimensions
 */
export const calculateChartDimensions = (containerDimensions, chartType) => {
  const { width: containerWidth, height: containerHeight } = containerDimensions;
  
  // Base dimensions with responsive scaling
  const baseConfigs = {
    line: {
      aspectRatio: 16/9,
      minWidth: 300,
      minHeight: 200,
      padding: { top: 20, right: 30, bottom: 40, left: 60 }
    },
    radar: {
      aspectRatio: 1,
      minWidth: 250,
      minHeight: 250,
      padding: { top: 20, right: 20, bottom: 20, left: 20 }
    },
    bar: {
      aspectRatio: 4/3,
      minWidth: 300,
      minHeight: 225,
      padding: { top: 20, right: 30, bottom: 60, left: 60 }
    }
  };
  
  const config = baseConfigs[chartType] || baseConfigs.line;
  
  // Calculate optimal dimensions
  let optimalWidth = Math.max(config.minWidth, containerWidth * 0.9);
  let optimalHeight = Math.max(config.minHeight, optimalWidth / config.aspectRatio);
  
  // Ensure it fits in container
  if (optimalHeight > containerHeight * 0.8) {
    optimalHeight = containerHeight * 0.8;
    optimalWidth = optimalHeight * config.aspectRatio;
  }
  
  return {
    width: Math.round(optimalWidth),
    height: Math.round(optimalHeight),
    padding: config.padding,
    aspectRatio: config.aspectRatio
  };
};